/**
 * ATRIBUIR MOVIMIENTOS DE UNA CUENTA COMPARTIDA (BAN-T3, Ares #28212).
 *
 * Audiencia: el administrador en el computador, separando Exito Chino de Nexo con Argemiro O; tambien a 375 px.
 * Vive en Auditoria > Conciliacion banco > "Atribuir movimientos". Cada movimiento sin atribuir se resuelve en dos
 * clics (marcar + destino), uno por uno o varios a la vez. Los destinos y sus nombres los da el servidor.
 *
 * CONDICION DURA (BAN-T3): un negocio solo nunca ve el selector. La subpestana aparece solo si el servidor dice
 * multiNegocio y el rol es admin. Mientras A no publique la ruta (404), no aparece: se enciende sola.
 *
 * @module presentation/components/conciliacion/AtribucionBanco
 */

import { AtribucionBancoApiClient as ClienteReal } from '../../../infrastructure/api/AtribucionBancoApiClient.js';
import { normalizarDestinos, movimientosDelMes, mesesConMovimientos, sugerenciaValida, cuerpoAtribucion, resumenAtribucion } from '../../../application/conciliacion/atribucionBanco.js';
import { esc, plata, fechaCorta, fraseDelMotivo, ponerCss } from './BandejaPropuestasBanco.js';

/**
 * @param {object} o
 * @param {string} o.businessId    la cuenta (el negocio dueno del extracto)
 * @param {boolean} o.puedeDecidir  solo el rol admin
 * @param {object} [o.cliente]      AtribucionBancoApiClient (se cambia en las pruebas)
 */
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
/** '2026-05' -> 'mayo 2026' */
const nombreMes = (m) => { const x = /^(\d{4})-(\d{2})$/.exec(String(m || '')); return x ? `${MESES[Number(x[2]) - 1]} ${x[1]}` : String(m || ''); };

export function crearAtribucionBanco({ businessId, puedeDecidir, cliente = ClienteReal } = {}) {
  const st = {
    comprobado: false,
    visible: false,
    destinos: [],
    cargando: false,
    error: null,
    items: null,
    mes: '',
    marcados: new Set(),
    hechos: {},           // id -> destino atribuido en esta sesion (null = deshecho)
    mensaje: null,
    ocupado: false
  };
  let raiz = null;

  /** Pregunta al servidor si esta cuenta tiene mas de un negocio. Una sola vez. -> visible */
  async function comprobar() {
    if (st.comprobado) return st.visible;
    st.comprobado = true;
    if (!puedeDecidir) return (st.visible = false);
    const res = await cliente.destinos(businessId);
    if (!res.ok) return (st.visible = false); // sin la ruta (o sin permiso): la subpestana no aparece
    const d = normalizarDestinos(res.data);
    st.destinos = d.destinos;
    return (st.visible = d.multiNegocio);
  }

  async function cargar() {
    st.cargando = true; st.error = null; pintar();
    const res = await cliente.sinAtribuir(businessId);
    st.cargando = false;
    if (!res.ok) { st.error = fraseDelMotivo(res); pintar(); return; }
    st.items = res.data;
    const meses = mesesConMovimientos(st.items);
    if (!meses.includes(st.mes)) st.mes = meses[0] || '';
    pintar();
  }

  const nombreDe = (destino) => st.destinos.find((d) => d.destino === destino)?.nombre || destino;

  async function atribuir(ids, destino) {
    const movimientos = cuerpoAtribucion(ids, destino, st.hechos);
    if (!movimientos.length) return;
    st.ocupado = true; st.mensaje = null; pintar();
    const res = await cliente.atribuir({ businessId, movimientos });
    st.ocupado = false;
    if (!res.ok) { st.mensaje = { cls: 'bp-error', txt: fraseDelMotivo(res) }; pintar(); return; }
    for (const m of movimientos) { st.hechos[m.bankMovementId] = destino; st.marcados.delete(m.bankMovementId); }
    const d = res.data || {};
    const n = d.cambiados ?? d.Cambiados ?? movimientos.length;
    st.mensaje = { cls: 'bp-hecho', txt: destino === null ? `Deshecho: ${n} movimiento(s) quedan sin atribuir.` : `${n} movimiento(s) atribuidos a ${nombreDe(destino)}.` };
    pintar();
  }

  function pintar() {
    if (!raiz) return;
    if (!st.visible) { raiz.innerHTML = ''; return; }
    const meses = st.items ? mesesConMovimientos(st.items) : [];
    const movs = st.items ? movimientosDelMes(st.items, st.mes) : [];
    const r = resumenAtribucion(movs, st.hechos);
    const botones = (attrs, deshabilitado) => st.destinos.map((d) =>
      `<button type="button" class="btn ${d.esDueno ? 'btn-ghost' : 'btn-primary'} btn-sm" ${attrs} data-ab-destino="${esc(d.destino)}" ${deshabilitado ? 'disabled' : ''}>${esc(d.nombre)}</button>`).join('');
    raiz.innerHTML = `
      <div class="bp-barra">
        ${st.items ? `<label>Mes<select data-ab-mes>${meses.map((m) => `<option value="${esc(m)}" ${m === st.mes ? 'selected' : ''}>${esc(nombreMes(m))}</option>`).join('')}</select></label>` : ''}
        <button type="button" class="btn btn-primary btn-sm" data-ab-cargar ${st.cargando ? 'disabled' : ''}>${st.cargando ? 'Cargando…' : (st.items ? 'Actualizar' : 'Ver sin atribuir')}</button>
      </div>
      ${st.error ? `<div class="bp-error">${esc(st.error)}</div>` : ''}
      ${st.items ? `
        <div class="bp-mes">${esc(nombreMes(st.mes) || 'Ningun mes')}: ${r.total} movimiento(s) llegaron sin atribuir.
          <div class="bp-cuentas"><span><b>${r.pendientes}</b> por atribuir</span><span><b>${r.atribuidos}</b> atribuidos ahora</span>
          ${Object.entries(r.porDestino).map(([k, v]) => `<span><b>${v}</b> ${esc(k === 'null' ? 'deshechos' : nombreDe(k))}</span>`).join('')}</div>
        </div>
        ${movs.length ? `<div class="bp-acciones" style="align-items:center">
            <label style="font-size:.875rem;color:#c9d1d9;display:flex;gap:6px;align-items:center"><input type="checkbox" data-ab-todos ${st.marcados.size && movs.filter((m) => !st.hechos[m.id]).every((m) => st.marcados.has(m.id)) ? 'checked' : ''}> Marcar los pendientes</label>
            <span style="font-size:.875rem;color:#8b949e">${st.marcados.size} marcado(s) →</span>
            ${botones('data-ab-lote', st.ocupado || !st.marcados.size)}
          </div>` : '<p class="empty-state">No quedan movimientos sin atribuir en ese mes.</p>'}
        ${st.mensaje ? `<div class="${st.mensaje.cls}">${esc(st.mensaje.txt)}</div>` : ''}
        ${movs.map((m) => {
          const hecho = Object.prototype.hasOwnProperty.call(st.hechos, m.id) ? st.hechos[m.id] : undefined;
          const sug = sugerenciaValida(m, st.destinos);
          return `<article class="bp-dia" data-ab-mov="${esc(m.id)}">
            <div class="bp-cab" style="cursor:default">
              ${hecho === undefined || hecho === null ? `<input type="checkbox" data-ab-marca="${esc(m.id)}" ${st.marcados.has(m.id) ? 'checked' : ''} aria-label="Marcar movimiento">` : ''}
              <span class="bp-fecha">${fechaCorta(m.fecha)}</span>
              <span style="font-size:.875rem;color:${m.valor < 0 ? '#f85149' : '#3fb950'}">${m.valor < 0 ? 'sale ' : 'entra '}${plata(Math.abs(m.valor))}</span>
              ${hecho ? `<span class="bp-estado ok">${esc(nombreDe(hecho))}</span>` : sug ? `<span class="bp-estado cuadra" title="${esc(m.motivo || '')}">Sugerido: ${esc(nombreDe(sug))}</span>` : ''}
            </div>
            <p class="bp-texto">${esc(m.descripcion || 'sin descripcion')}</p>
            ${sug && !hecho && m.motivo ? `<p class="bp-texto" style="color:#8b949e">${esc(m.motivo)}</p>` : ''}
            <div class="bp-acciones">${hecho
              ? `<button type="button" class="btn btn-ghost btn-sm" data-ab-deshacer="${esc(m.id)}" ${st.ocupado ? 'disabled' : ''}>Deshacer</button>`
              : botones(`data-ab-uno="${esc(m.id)}"`, st.ocupado)}</div>
          </article>`;
        }).join('')}
      ` : ''}`;
    enlazar(movs);
  }

  function enlazar(movs) {
    raiz.querySelector('[data-ab-cargar]')?.addEventListener('click', cargar);
    raiz.querySelector('[data-ab-mes]')?.addEventListener('change', (e) => { st.mes = e.target.value; st.marcados.clear(); st.mensaje = null; pintar(); });
    raiz.querySelector('[data-ab-todos]')?.addEventListener('change', (e) => {
      st.marcados.clear();
      if (e.target.checked) for (const m of movs) if (!st.hechos[m.id]) st.marcados.add(m.id);
      pintar();
    });
    raiz.querySelectorAll('[data-ab-marca]').forEach((c) => c.addEventListener('change', () => {
      const id = c.dataset.abMarca;
      if (c.checked) st.marcados.add(id); else st.marcados.delete(id);
      pintar();
    }));
    raiz.querySelectorAll('[data-ab-lote]').forEach((b) => b.addEventListener('click', () => atribuir([...st.marcados], b.dataset.abDestino)));
    raiz.querySelectorAll('[data-ab-uno]').forEach((b) => b.addEventListener('click', () => atribuir([b.dataset.abUno], b.dataset.abDestino)));
    raiz.querySelectorAll('[data-ab-deshacer]').forEach((b) => b.addEventListener('click', () => atribuir([b.dataset.abDeshacer], null)));
  }

  return {
    estado: st,
    comprobar,
    cargar,
    /** Pinta en el elemento (se puede llamar en cada render del padre: el estado se conserva). */
    montar(el) {
      if (!el) return;
      raiz = el;
      raiz.classList.add('ban-prop');
      ponerCss(el.ownerDocument);
      pintar();
    }
  };
}
